import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useSidebar } from '@/components/ui/sidebar'
import {
  GUIDED_TOUR_START,
  consumePendingTourStart,
  isGuidedTourCompleted,
  markGuidedTourCompleted,
} from '@/lib/guided-tour'

type TourStep = {
  target: string
  title: string
  body: string
}

const STEPS: TourStep[] = [
  {
    target: 'tour-welcome',
    title: 'ברוכים הבאים למערכת הגיוס',
    body: 'זהו סיור קצר על המסכים שתשתמשו בהם ביום־יום. אפשר לדלג עכשיו ולחזור להדרכה בכל זמן.',
  },
  {
    target: 'tour-sidebar',
    title: 'תפריט הניווט',
    body: 'התפריט בצד ימין מרכז את כל הפעולות: לוח הבקרה, המועמדים והשאלונים.',
  },
  {
    target: 'tour-nav-dashboard',
    title: 'לוח בקרה',
    body: 'מסך הבית. מכאן מתחילים, ורואים תמונת מצב של הגיוס.',
  },
  {
    target: 'tour-stats',
    title: 'מדדי הגיוס',
    body: 'הכרטיסים מסכמים מועמדים, שאלונים, מענה שממתין ושאלונים שהושלמו.',
  },
  {
    target: 'tour-nav-candidates',
    title: 'מועמדים',
    body: 'פתחו את «מועמדים» כדי לעיין ברשימה או להעלות קורות חיים.',
  },
  {
    target: 'tour-nav-forms',
    title: 'שאלונים',
    body: 'כאן יוצרים שאלון, שולחים מבחן מוכן ועוקבים אחרי שאלונים שנשלחו.',
  },
  {
    target: 'tour-nav-settings',
    title: 'הגדרות',
    body: 'בהגדרות מעדכנים את פרטי החשבון שלכם.',
  },
  {
    target: 'tour-nav-managers',
    title: 'מנהלים מקצועיים',
    body: 'כאן מנהלים את רשימת המנהלים המקצועיים במערכת.',
  },
  {
    target: 'tour-nav-users',
    title: 'משתמשים',
    body: 'מכאן מנהל־העל יוצר ומנהל חשבונות מגייסים.',
  },
  {
    target: 'tour-help',
    title: 'חזרה על ההדרכה',
    body: 'הסיור מוצג פעם אחת לכל משתמש. כדי להפעיל אותו שוב, לחצו על «הדרכה» בכותרת.',
  },
]

function visibleSteps() {
  return STEPS.filter((step) => document.querySelector(`[data-tour="${step.target}"]`))
}

function computePosition(rect: DOMRect | null, cardW: number, cardH: number) {
  const margin = 16
  const vw = window.innerWidth
  const vh = window.innerHeight
  if (!rect || rect.width < 2 || rect.height < 2) {
    return {
      top: Math.max(margin, (vh - cardH) / 2),
      left: Math.max(margin, (vw - cardW) / 2),
    }
  }

  const gap = 14
  const onRight = rect.left > vw * 0.55
  let top = onRight ? rect.top : rect.bottom + gap
  let left = onRight ? rect.left - cardW - gap : rect.right - cardW

  if (left < margin) left = Math.min(rect.right + gap, vw - cardW - margin)
  if (left + cardW > vw - margin) left = vw - cardW - margin
  if (left < margin) left = margin
  if (top + cardH > vh - margin) top = Math.max(margin, rect.top - cardH - gap)
  if (top < margin) top = margin
  return { top, left }
}

export function GuidedTour() {
  const { recruiter } = useAuth()
  const location = useLocation()
  const { setOpen, setOpenMobile, isMobile } = useSidebar()
  const [active, setActive] = useState(false)
  const [steps, setSteps] = useState<TourStep[]>([])
  const [index, setIndex] = useState(0)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [pos, setPos] = useState({ top: 24, left: 24 })
  const cardRef = useRef<HTMLDivElement>(null)
  const beginRef = useRef<() => void>(() => {})
  const autoStarted = useRef(false)

  const begin = useCallback(() => {
    consumePendingTourStart()
    setOpen(true)
    if (isMobile) setOpenMobile(true)
    window.setTimeout(() => {
      const next = visibleSteps()
      if (next.length === 0) return
      setSteps(next)
      setIndex(0)
      setActive(true)
    }, isMobile ? 320 : 40)
  }, [isMobile, setOpen, setOpenMobile])

  beginRef.current = begin

  useEffect(() => {
    if (!recruiter || location.pathname !== '/dashboard' || autoStarted.current) return
    const pending = consumePendingTourStart()
    if (!pending && isGuidedTourCompleted(recruiter.uuid)) return
    autoStarted.current = true
    const timer = window.setTimeout(() => beginRef.current(), 450)
    return () => window.clearTimeout(timer)
  }, [recruiter, location.pathname])

  useEffect(() => {
    const onStart = () => {
      if (location.pathname === '/dashboard') beginRef.current()
    }
    window.addEventListener(GUIDED_TOUR_START, onStart)
    return () => window.removeEventListener(GUIDED_TOUR_START, onStart)
  }, [location.pathname])

  const step = steps[index]

  useEffect(() => {
    if (!active || !step) return

    const measure = () => {
      const el = document.querySelector(`[data-tour="${step.target}"]`)
      setRect(el ? el.getBoundingClientRect() : null)
    }

    document.querySelector(`[data-tour="${step.target}"]`)?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    })
    const frame = window.requestAnimationFrame(measure)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [active, step])

  useLayoutEffect(() => {
    if (!active) return
    const card = cardRef.current
    if (!card) return
    const { width, height } = card.getBoundingClientRect()
    const next = computePosition(rect, width, height)
    setPos((prev) =>
      Math.abs(prev.top - next.top) < 1 && Math.abs(prev.left - next.left) < 1 ? prev : next
    )
  }, [active, rect, index, steps])

  const close = useCallback(
    (completed: boolean) => {
      if (completed && recruiter) markGuidedTourCompleted(recruiter.uuid)
      setActive(false)
    },
    [recruiter]
  )

  useEffect(() => {
    if (!active) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(true)
      if (event.key === 'ArrowLeft') {
        setIndex((current) => Math.max(current - 1, 0))
      }
      if (event.key === 'ArrowRight') {
        setIndex((current) => Math.min(current + 1, steps.length - 1))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active, close, steps.length])

  const isLast = index === steps.length - 1
  const spotlight = useMemo(() => {
    if (!rect || rect.width < 2 || rect.height < 2) return null
    const pad = 6
    return {
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
    }
  }, [rect])

  if (!active || !step) return null

  return (
    <div className="fixed inset-0 z-[100]" dir="rtl">
      <div className={spotlight ? 'absolute inset-0' : 'absolute inset-0 bg-black/55'} />
      {spotlight && (
        <div
          className="pointer-events-none absolute rounded-lg ring-2 ring-primary"
          style={{
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
          }}
        />
      )}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="guided-tour-title"
        className="absolute w-[min(22rem,calc(100vw-2rem))] rounded-xl border bg-card p-4 text-card-foreground shadow-xl"
        style={{ top: pos.top, left: pos.left }}
      >
        <p className="text-xs text-muted-foreground">
          {index + 1} מתוך {steps.length}
        </p>
        <h2 id="guided-tour-title" className="mt-1 text-base font-semibold">
          {step.title}
        </h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{step.body}</p>
        <div className="mt-4 flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={() => close(true)}>
            דלג
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={index === 0}
              onClick={() => setIndex((current) => current - 1)}
            >
              הקודם
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (isLast) close(true)
                else setIndex((current) => current + 1)
              }}
            >
              {isLast ? 'סיום' : 'הבא'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
